<script lang="ts">
import CarouselDots from "@/Components/CarouselDots.vue";
import GenericButton from "@/Components/Generics/GenericButton.vue";
import Plane from "@/Components/Generics/Plane.vue";
import { defineComponent } from "vue";

export default defineComponent({
    name: "MainHeroVue",
    components: {
        Plane,
        GenericButton,
        CarouselDots,
    },
    props: {},
    emits: ["clickFind"],
    data: () => ({
        currentSlide: 0,
        images: ["imgs/main_1_edit.jpg", "imgs/main_2_edit.jpg", "imgs/main_3.jpg"],
        interval: null,
    }),
    computed: {
        locations(): string[] {
            const locationsString = this.$trans("main.locations-total");
            const result = locationsString.split(",").map((string) => string.trim());

            return result;
        },
    },
    mounted() {
        this.startInterval();
    },
    beforeUnmount() {
        clearInterval(this.interval);
    },
    methods: {
        splitHeadline(headline: string): { upper: string; lower: string } {
            const words = headline.trim().split(/\s+/);

            return {
                upper: words.slice(0, -1).join(" "),
                lower: words.at(-1) ?? "",
            };
        },

        startInterval(): void {
            this.interval = window.setInterval(() => {
                this.currentSlide = (this.currentSlide + 1) % this.images.length;
            }, 5000);
        },
        clearInterval(): void {
            if (this.interval !== null) {
                window.clearInterval(this.interval);
                this.interval = null;
            }
        },
        nextSlide(resetInterval: boolean): void {
            this.currentSlide = (this.currentSlide + 1) % this.images.length;
            if (resetInterval) {
                this.clearInterval();
                this.startInterval();
            }
        },
        prevSlide(resetInterval: boolean): void {
            this.currentSlide = (this.currentSlide - 1 + this.images.length) % this.images.length;
            if (resetInterval) {
                this.clearInterval();
                this.startInterval();
            }
        },
        setSlide(index: number): void {
            this.currentSlide = index;
            this.clearInterval();
            this.startInterval();
        },

        onClickFind(): void {
            this.$emit("clickFind");
        },
        onClickSearch(): void {
            window.location.href = route("search.index");
        },
    },
});
</script>

<template>
    <div id="heroPlane" class="flex flex--v flex--width">
        <!-- CAROUSEL PLANE -->
        <Plane class="hero-plane flex--halign-e disable-pointer-events" :rounded="true" :darker="true">
            <template #background>
                <div class="hero-carousel">
                    <img v-for="(image, index) in images" :key="image" :src="image" :class="{ 'img--active': currentSlide === index }" />
                </div>
                <div class="carousel-space-buttons flex">
                    <button class="carousel-space-button" @click.stop="prevSlide(true)"></button>
                    <button class="carousel-space-button" @click.stop="nextSlide(true)"></button>
                </div>
            </template>
            <template #default>
                <div class="hero-content flex flex--width flex--height flex--h flex--halign-sb flex--valign-e disable-pointer-events">
                    <div class="hero-plane-content hero-plane-content--left flex--extend flex flex--v flex--valign-s flex--gap-20 disable-pointer-events">
                        <div class="flex flex--v disable-pointer-events">
                            <span class="text text--size-48 text--semi-bold text--color-white text--shadow-big">{{ splitHeadline($trans("main.headline")).upper }}</span>
                            <span class="text more-negative-margin text--size-56 text--extra-bold text--color-white text--shadow-big">{{ splitHeadline($trans("main.headline")).lower }}</span>
                        </div>
                        <div class="flex flex--v disable-pointer-events">
                            <span class="text text--size-18 text--height-26 text--regular text--color-white text--shadow-big">{{ $trans("main.text-1") }}</span>
                            <span class="text text--size-18 text--height-26 text--regular text--color-white text--shadow-big">{{ $trans("main.text-2") }}</span>
                        </div>
                        <GenericButton :title="$trans('search.btn_search')" :identifier="'djf_update_company'" @click="onClickFind" />
                    </div>

                    <CarouselDots class="carousel-dots" :length="images.length" :active="currentSlide" @click="setSlide" />

                    <div class="hero-plane-content hero-plane-content--right flex--extend flex flex--v flex--valign-e flex--gap-20 disable-pointer-events">
                        <div class="flex flex--v disable-pointer-events">
                            <span class="text text--size-48 text--semi-bold text--color-white text--shadow-big text--right">{{ splitHeadline($trans("main.search-headline")).upper }}</span>
                            <span class="more-negative-margin text text--size-56 text--extra-bold text--color-white text--shadow-big text--right">{{ splitHeadline($trans("main.search-headline")).lower }}</span>
                        </div>
                        <div class="flex flex--v disable-pointer-events">
                            <span class="text text--size-18 text--height-26 text--regular text--color-white text--shadow-big text--right">{{ $trans("main.search-text-1") }}</span>
                            <span class="text text--size-18 text--height-26 text--regular text--color-white text--shadow-big text--right">{{ $trans("main.search-text-2") }}</span>
                        </div>
                        <GenericButton :title="$trans('search.btn_search_function')" :identifier="'djf_update_search'" :secondary="true" @click="onClickSearch" />
                    </div>
                </div>
            </template>
        </Plane>

        <!-- LOCATIONS PLANE -->
        <Plane class="hero-sub-plane" :rounded="true">
            <div class="flex flex--valign-c flex--halign-c flex--wrap flex--gap-10">
                <template v-for="(location, idx) in locations" :key="location">
                    <span v-if="idx > 0" class="text text--size-16 text--primary-b text--less-gap">&bull;</span>
                    <span class="text text--size-16 text--medium text--primary-b text--less-gap">
                        {{ location }}
                    </span>
                </template>
            </div>
        </Plane>
    </div>
</template>

<style lang="scss" scoped>
.hero-carousel {
    position: absolute;
    inset: 0;

    &::before {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(to right, rgba($color-secondary-b, 0.75) 20%, #fff0 60%);
        z-index: 1;
    }
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        background: linear-gradient(to left, rgba($color-primary-a, 0.75) 20%, #fff0 60%);
        z-index: 1;
    }

    img {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;

        object-fit: cover;
        opacity: 0;
        transition: opacity 0.8s ease-in-out;

        &.img--active {
            opacity: 1;
        }
    }
}
.carousel-space-buttons {
    position: absolute;
    inset: 0;
    width: 100%;
    .carousel-space-button {
        width: 100%;
        cursor: pointer;
        pointer-events: all;
        opacity: 0;
    }
}
.hero-plane {
    min-height: 550px;
    z-index: 1;

    .hero-content {
        position: relative;
    }

    .hero-plane-content {
        max-width: 45%;
        padding: rem(25);
        z-index: 2;
    }

    .carousel-dots {
        position: absolute;
        left: 50%;
        bottom: rem(-5);
        transform: translateX(-50%);
        z-index: 3;
    }
}

@include media-breakpoint-down(lg) {
    .hero-plane {
        min-height: rem(520);
        padding: rem(15);
        padding-bottom: rem(20);

        .hero-plane-content {
            max-width: 48%;
            padding: rem(20);
        }
    }
}

@include media-breakpoint-down(md) {
    .hero-carousel {
        &::before {
            background: linear-gradient(to right bottom, rgba($color-secondary-b, 0.75) 20%, #fff0 60%);
        }
        &::after {
            background: linear-gradient(to left top, rgba($color-primary-a, 0.75) 20%, #fff0 60%);
        }
    }
    .hero-plane {
        min-height: rem(620);
        padding: rem(5);
        padding-bottom: rem(20);

        .hero-content {
            flex-direction: column;
            justify-content: center;
            align-items: stretch;
            gap: rem(25);
            padding: rem(30) rem(25) rem(55);
        }

        .hero-plane-content {
            max-width: 100%;
            width: 100%;
            padding: 0;
            align-items: flex-start !important;
        }

        .hero-plane-content--right {
            align-items: flex-end !important;
        }
    }
}

@include media-breakpoint-down(sm) {
    .hero-carousel {
        &::before {
            background: linear-gradient(to bottom, rgba($color-secondary-b, 0.75) 20%, #fff0 60%);
        }
        &::after {
            background: linear-gradient(to top, rgba($color-primary-a, 0.75) 20%, #fff0 60%);
        }
    }
    .hero-plane {
        min-height: rem(630);

        .hero-content {
            padding: rem(25) rem(18) rem(80);
            gap: rem(60);
        }

        .hero-plane-content {
            gap: rem(12);
        }
    }

    .hero-sub-plane {
        margin-top: rem(-30);
        padding-top: rem(50);
    }
}

.hero-sub-plane {
    margin-top: -40px;
    padding-top: 65px;
}
.more-negative-margin {
    margin-top: -0.3rem;
}
.disable-pointer-events {
    pointer-events: none;
}
</style>
